import React, { useState } from 'react';
import {
  useDisclosure,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Drawer,
  DrawerFooter,
  IconButton,
  Icon,
  Tooltip,
  Text,
  HStack,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { FiFilePlus } from 'react-icons/all';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  ZeroCarbonProject,
  ZeroCarbonProjectInvoice,
} from '~/types/zeroCarbon';
import { Button, Datepicker, Heading, Input } from '~/components';
import { InputFloat } from '~/components/InputFloat';
import { FileUploader } from '~/components/FileInput';
import { createProjectInvoice } from '~/services/ZeroCarbon';
import { queryClient } from '~/utils/react-query';

interface NewProjectInvoiceDialogProps {
  projeto: ZeroCarbonProject;
  carregarNotasFiscais: () => void;
}

const ProjectInvoiceFormSchema = yup.object().shape({
  numero: yup
    .string()
    .required('Número da nota fiscal obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  data_compra: yup.string().required('Data da compra obrigatória'),
  valor: yup
    .number()
    .required('Valor obrigatório')
    .min(0.01, 'Valor deve ser maior que zero'),
  quantidade_co2_contratado: yup
    .number()
    .required('Quantidade de CO2e obrigatória')
    .min(0.01, 'Quantidade deve ser maior que zero'),
  anexo_nota_fiscal_nome: yup.string().required('Anexo obrigatório'),
});

export const NewProjectInvoiceDialog: React.FC<NewProjectInvoiceDialogProps> =
  ({ projeto, carregarNotasFiscais }: NewProjectInvoiceDialogProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { setError, formState, clearErrors } = useForm({
      resolver: yupResolver(ProjectInvoiceFormSchema),
    });
    const { errors } = formState;
    const toast = useToast();
    const [notaFiscal, setNotaFiscal] = useState<ZeroCarbonProjectInvoice>({
      numero: '',
      data_compra: undefined,
      valor: undefined,
      quantidade_co2_contratado: 0,
      anexo_nota_fiscal_nome: '',
      anexo_nota_fiscal: undefined,
    } as ZeroCarbonProjectInvoice);
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const handleOpen = () => {
      onOpen();
    };

    const handleClose = () => {
      onClose();
    };

    const limparAnexoNotaFiscal = () => {
      setNotaFiscal({
        ...notaFiscal,
        anexo_nota_fiscal: undefined,
        anexo_nota_fiscal_nome: '',
      });
    };

    const possuiErros = () => {
      try {
        ProjectInvoiceFormSchema.validateSync(notaFiscal, {
          abortEarly: false,
        });
        clearErrors();
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          err.inner.forEach(e => {
            setError(e.path!, { message: e.message });
          });
          return true;
        }
      }
      return false;
    };

    const limparValores = () => {
      setNotaFiscal({
        numero: '',
        data_compra: undefined,
        valor: undefined,
        quantidade_co2_contratado: 0,
        anexo_nota_fiscal_nome: '',
        anexo_nota_fiscal: undefined,
      });
    };

    const salvar = () => {
      if (possuiErros()) {
        return;
      }
      const formData = new FormData();
      formData.append('numero', notaFiscal.numero);
      formData.append(
        'data_compra',
        `${notaFiscal.data_compra?.toISOString()}`,
      );
      formData.append('valor', `${notaFiscal.valor}`);
      formData.append(
        'quantidade_co2_contratado',
        `${notaFiscal.quantidade_co2_contratado}`,
      );
      formData.append('anexo', notaFiscal.anexo_nota_fiscal!);

      setIsFetching(true);
      createProjectInvoice(projeto.id, formData)
        .then(() => {
          toast({
            title: 'Nota fiscal cadastrada com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          limparValores();
          handleClose();
          carregarNotasFiscais();
        })
        .catch(_err => {
          const errorsValidate = Object.entries(_err.response.data);
          errorsValidate.forEach(_er => {
            toast({
              title: 'Erro ao cadastrar nota fiscal',
              description: String(_er[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        })
        .finally(() => {
          setIsFetching(false);
        });
    };

    return (
      <>
        <Tooltip hasArrow label="Adicionar nota fiscal" bg="black">
          <IconButton
            me={1}
            size="sm"
            aria-label="Adicionar nota fiscal"
            icon={<Icon as={FiFilePlus} />}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleOpen();
            }}
          />
        </Tooltip>
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={handleClose}
          size="lg"
          closeOnOverlayClick={false}
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader mb={4}>
              <Heading fontSize="xl">Cadastrar nota fiscal</Heading>
            </DrawerHeader>

            <DrawerBody>
              <Stack gridGap={3}>
                <Text size="md" fontWeight="bold">
                  {projeto.nome}
                </Text>
                <Input
                  value={notaFiscal.numero}
                  label="N° da Nota fiscal"
                  name="numero"
                  isRequired
                  type="text"
                  error={errors.numero}
                  onChange={e => {
                    clearErrors('numero');
                    setNotaFiscal({ ...notaFiscal, numero: e.target.value });
                  }}
                  isDisabled={isFetching}
                />
                <Datepicker
                  label="Data da compra"
                  required
                  onChange={e => {
                    clearErrors('data_compra');
                    setNotaFiscal({
                      ...notaFiscal,
                      data_compra: e || undefined,
                    });
                  }}
                  name="data_compra"
                  selectedDate={notaFiscal.data_compra}
                  error={errors.data_compra}
                  isDisabled={isFetching}
                />
                <HStack alignItems="top">
                  <InputFloat
                    value={notaFiscal.valor}
                    label="Valor (R$)"
                    name="valor"
                    isRequired
                    error={errors.valor}
                    onChangeValue={e => {
                      clearErrors('valor');
                      setNotaFiscal({ ...notaFiscal, valor: e });
                    }}
                    isDisabled={isFetching}
                  />
                  <Input
                    value={Number(
                      notaFiscal.quantidade_co2_contratado,
                    ).toLocaleString('pt-BR', {
                      maximumFractionDigits: 0,
                    })}
                    label="Quantidade de CO2e (t)"
                    name="quantidade_co2_contratado"
                    isRequired
                    error={errors.quantidade_co2_contratado}
                    type="text"
                    inputMode="numeric"
                    onChange={e => {
                      clearErrors('quantidade_co2_contratado');
                      const value = e.target.value.replace(/\D/g, '');
                      setNotaFiscal({
                        ...notaFiscal,
                        quantidade_co2_contratado: Number(value),
                      });
                    }}
                    isDisabled={isFetching}
                  />
                </HStack>
                <FileUploader
                  isRequired
                  fileName={notaFiscal.anexo_nota_fiscal_nome}
                  onChange={file => {
                    clearErrors('anexo_nota_fiscal_nome');
                    setNotaFiscal({
                      ...notaFiscal,
                      anexo_nota_fiscal: file,
                      anexo_nota_fiscal_nome: file.name,
                    });
                  }}
                  isDisabled={isFetching}
                  clearFile={() => limparAnexoNotaFiscal()}
                  label="Anexar nota fiscal"
                  showButton={false}
                  helpText="Arquivo do tipo pdf. O limite total do arquivo é de 5MB."
                  errors={errors.anexo_nota_fiscal_nome}
                />
              </Stack>
            </DrawerBody>

            <DrawerFooter>
              <Button variant="secondary" onClick={() => handleClose()}>
                Fechar
              </Button>
              <Button
                variant="primary"
                onClick={salvar}
                ms={2}
                isDisabled={isFetching}
              >
                Salvar Alterações
                {isFetching && <Spinner size="md" ml={2} />}
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </>
    );
  };
